import { CheckCircle2 } from "lucide-react";
import MenuLinks from "./MenuLinks";
import { fdate, fmt, weekday } from "../lib/util";
import { Alert, Badge, Card, SectionTitle, Table, Empty } from "../components/kit";
import { useSchool } from "./ctx";
import { Downloads } from "./Forms";
import PlanPanel from "./PlanPanel";

function Conferencia({ stock }) {
  const menu = new Set(stock.menu_products || []);
  const af = new Set(stock.af_products || []);
  const rows = stock.rows.filter((r) => r.opening + r.entradas > 0.0001).map((r) => ({ ...r, left: r.opening + r.entradas - r.saidas_est }));
  const bad = rows.filter((r) => Math.abs(r.left) > 0.01);
  if (!rows.length) return null;
  return (
    <Card data-testid="daily-check">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-heading font-bold text-lg text-[#0B192C]">Conferência: tudo o que foi comprado foi distribuído?</h3>
        {bad.length ? (
          <Badge tone="red" data-testid="daily-check-status" style={{ whiteSpace: "normal" }}>{bad.length} produto(s) com sobra ou falta</Badge>
        ) : (
          <Badge tone="green" data-testid="daily-check-status" style={{ whiteSpace: "normal" }}><CheckCircle2 className="w-3.5 h-3.5" />Todos os {rows.length} produtos distribuídos · estoque final zerado</Badge>
        )}
      </div>
      <Table testid="daily-check-table">
        <thead><tr><th>Gênero</th><th>Unidade</th><th className="num">Estoque anterior</th><th className="num">Entradas (notas)</th><th className="num">Distribuído no controle diário</th><th className="num">Estoque final</th><th>Distribuição</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.product_id} data-testid={`daily-check-row-${r.product_id}`} className={Math.abs(r.left) > 0.01 ? "!bg-red-50" : ""}>
              <td className="font-semibold">{r.name}</td>
              <td>{r.unit}</td>
              <td className="num">{fmt(r.opening)}</td>
              <td className="num">{fmt(r.entradas)}</td>
              <td className="num">{fmt(r.saidas_est)}</td>
              <td className={`num font-bold ${Math.abs(r.left) > 0.01 ? "text-red-700" : "text-green-700"}`}>{fmt(Math.abs(r.left) > 0.01 ? r.left : 0)}</td>
              <td className="text-xs">{menu.has(r.name) ? "conforme o cardápio SEEDUC" : af.has(r.name) ? "agricultura familiar — todos os dias letivos do mês" : "a partir da emissão da nota"}{menu.has(r.name) && af.has(r.name) ? " (agricultura familiar, desde o dia 1)" : ""}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

export default function DailyControl() {
  const { stock, products, setSection } = useSchool();
  if (!stock) return null;
  const pmap = Object.fromEntries(products.map((p) => [p.id, p]));
  const dates = [...new Set([...Object.keys(stock.daily_est), ...Object.keys(stock.daily_real)])].sort();
  return (
    <div className="space-y-4">
      <SectionTitle title="Controle diário de gêneros" subtitle="Saídas por dia em cada base. A base usada nos totais do mês está destacada.">
        <Downloads form="controle_diario" label="controle diário" />
      </SectionTitle>
      <PlanPanel />
      <Alert tone="info">
        A coluna <b>Modo IA (A)</b> distribui todos os gêneros comprados nas notas fiscais dos fornecedores e da agricultura familiar (e a sobra do mês anterior). Cada nota é consumida da data de emissão até a próxima compra do mesmo produto; as notas da agricultura familiar valem desde o primeiro dia letivo do mês da nota (entregas ao longo do mês). Produtos que aparecem no cardápio SEEDUC saem nos dias em que o cardápio os usa, os demais saem a partir da data de emissão da nota (ou todos os dias, se forem da agricultura familiar). Tudo é consumido dentro do mês (estoque final zero).
        A coluna <b>Retiradas (B)</b> é a saída líquida registrada (retirada − devolução). Para editar as retiradas: <button className="underline font-semibold" onClick={() => setSection("withdrawals")}>Retiradas semanais</button>.
      </Alert>
      <Conferencia stock={stock} />
      <MenuLinks />
      <Table testid="daily-table">
        <thead><tr><th>Data</th><th>Gênero</th><th>Unidade</th><th className={`num ${stock.base === "estimativa" ? "!text-[#C2410C]" : ""}`}>Modo IA (A)</th><th className={`num ${stock.base === "real" ? "!text-[#C2410C]" : ""}`}>Retiradas reais (B)</th></tr></thead>
        <tbody>
          {dates.flatMap((d) => {
            const pids = [...new Set([...Object.keys(stock.daily_est[d] || {}), ...Object.keys(stock.daily_real[d] || {})])].sort((a, b) => (pmap[a]?.name || "").localeCompare(pmap[b]?.name || ""));
            return pids.map((p, i) => (
              <tr key={d + p}>
                <td className="whitespace-nowrap">{i === 0 && <>{fdate(d)} <span className="text-xs text-slate-400">{weekday(d)}</span>{!(d in stock.days) && <Badge tone="amber">fora do atendimento</Badge>}</>}</td>
                <td>{pmap[p]?.name || "?"}</td>
                <td>{pmap[p]?.unit}</td>
                <td className={`num ${stock.base === "estimativa" ? "font-bold" : "text-slate-400"}`}>{fmt(stock.daily_est[d]?.[p])}</td>
                <td className={`num ${stock.base === "real" ? "font-bold" : "text-slate-400"}`}>{fmt(stock.daily_real[d]?.[p])}</td>
              </tr>
            ));
          })}
        </tbody>
      </Table>
      {!dates.length && <Empty>Sem saídas no mês. Verifique cardápio, comensais, calendário e estoque disponível.</Empty>}
      {stock.pendencias.length > 0 && <Alert title="Pendências do Modo IA">{stock.pendencias.map((p) => <div key={p}>{p}</div>)}</Alert>}
    </div>
  );
}
